import { patch } from "@rails/request.js"

// CONEK-8802 — the swipe pager's background saves.
//
// The swipe pager keeps every page of an inspection in one document and saves
// the page the inspector leaves without navigating: it PATCHes the page's form
// to its usual endpoint with `direction=stay`, which Inspections::PagerBackgroundSave
// answers with no redirect (200 + `Conek-Page-Saved: <page id>`, or 422 +
// `Conek-Save-Error: <code>`), plus a Turbo Stream request.js renders.
//
// One save is in flight at a time: Inspections::Score rewrites every page's
// entry on each save, so two in parallel would only fight over the same rows.
// The queue holds the LATEST state per page, not a log — SavePage is a
// whole-page, idempotent write — and the body is read from the form when the
// save is sent, so an edit made while the page waited is not lost. An edit made
// while its save is in flight re-queues the page (the generation moves on), so
// it is sent again.
//
// A save counts only when the response names its page. A followed redirect
// would end on whatever page it points at — the login page after an expired
// session — and a 200 there says nothing about the save, so redirects are not
// followed (`redirect: "manual"`), the same lesson as the Android queue's
// Conek-Write-Accepted (CONEK-8279).
//
// CONEK-8802 (offline capture) — answers the pager kept on the device go out
// again as `replay`: the save carries `replay=1` and the screen that captured
// them — the caller says, on every enqueue, whether the page is still such a
// replay or has become an edit made here. When the server holds
// a newer change from elsewhere it writes nothing and answers 200 with
// `Conek-Page-Superseded`: the page is done with, and `onSuperseded` tells the
// pager to put it back as rendered. `onSend(pageId)` may return a token (the
// draft revision being sent) that `onSaved` / `onSuperseded` get back once the
// server has answered for it.
//
// States reported through `onState`: "saving", "saved", "offline" (no answer;
// retried with backoff and as soon as the browser is back online),
// "retrying" (a server error, or a refusal the server marks as temporary with
// a 5xx — a deadlock, a timeout; retried with the same backoff), "failed"
// (the server refused a page, which is dropped since retrying would not
// help), "stopped" (the inspection can no longer be saved: no longer in
// progress, or the user may not save it) and "signed-out" (the session
// ended). The last two stop the queue for good, as does `destroy`.

export const SAVED_HEADER = "Conek-Page-Saved"
export const ERROR_HEADER = "Conek-Save-Error"
export const SUPERSEDED_HEADER = "Conek-Page-Superseded"

const TERMINAL_ERRORS = new Set(["not_in_progress", "unauthorized"])
const RETRY_DELAYS_MS = [2000, 5000, 15000, 30000]
// A save that has had no answer by then is given up and retried: a stalled
// connection (a slow proxy, a hung backend) neither fails nor resolves, and
// would leave the queue — and the Review waiting on it — stuck on "Saving…".
export const SAVE_TIMEOUT_MS = 20000

export class PageSaveQueue {
  constructor({
    onState = () => undefined,
    activePageId = () => null,
    onSend = () => undefined,
    onSaved = () => undefined,
    onSuperseded = () => undefined,
  } = {}) {
    this.onState = onState
    this.activePageId = activePageId
    this.onSend = onSend
    this.onSaved = onSaved
    this.onSuperseded = onSuperseded
    this.pending = new Map()
    this.generation = 0
    this.running = false
    this.stopped = false
    this.state = "idle"
    // Every page whose last save was refused, until a save of it lands: one
    // refused page must not be forgotten because another was refused after.
    this.failedPages = new Set()
    this.retryIndex = 0
    this.waiters = []

    this.onOnline = () => this.retryNow()
    window.addEventListener("online", this.onOnline)
  }

  // The pager is going away. A save still in flight finishes on its own, but
  // nothing is retried or sent after it: a later pager on this page has its
  // own queue, and a stale retry from this one landing after the new queue's
  // save would put the old answers back.
  destroy() {
    this.destroyed = true
    this.stopped = true
    this.pending.clear()
    window.removeEventListener("online", this.onOnline)
    clearTimeout(this.retryTimer)
    this.waiters = []
  }

  get failedPageId() {
    return this.failedPages.values().next().value || null
  }

  get size() {
    return this.pending.size
  }

  // Queue `form`'s page. `complete` marks it completed on the server — the
  // inspector left it — and sticks until that save lands. `replay` sends it as
  // a replay of answers kept on the device; the latest call decides.
  enqueue(pageId, form, { complete = false, replay = false } = {}) {
    if (this.hold(pageId, form, { complete, replay })) this.run()
  }

  // Queue a page without starting a save — for a page about to be sent by
  // flushOnLeave. Returns whether it was queued.
  hold(pageId, form, { complete = false, replay = false } = {}) {
    if (this.stopped || !pageId || !form) return false
    const queued = this.pending.get(pageId)
    this.generation += 1
    this.pending.set(pageId, {
      form,
      complete: complete || Boolean(queued?.complete),
      replay,
      generation: this.generation,
    })
    return true
  }

  // Resolves once nothing is left to send, or as soon as the queue cannot get
  // there right now (offline, a refused page, stopped): {ok, failedPageId,
  // state}. `except` is a page whose refusal does not count — the page on
  // screen, which the caller is about to submit itself. Asked while offline,
  // it tries at once rather than waiting out the backoff: the inspector is
  // asking now, and the connection may well be back.
  drain({ except = null } = {}) {
    return new Promise((resolve) => {
      this.waiters.push(({ pending, failed, stopped, state }) => {
        const refused = failed.filter((pageId) => pageId !== except)
        resolve({
          ok: pending === 0 && !stopped && refused.length === 0,
          failedPageId: refused[0] || null,
          state,
        })
      })
      if (this.running) return
      if (this.pending.size > 0 && !this.stopped) {
        clearTimeout(this.retryTimer)
        this.run()
      } else {
        this.settleWaiters()
      }
    })
  }

  // The page is going away: send everything still queued with `keepalive`, so
  // the browser delivers it even after the document is gone. Entries stay
  // queued; if the page survives (it was only hidden), the normal run resends
  // them, which SavePage makes harmless.
  flushOnLeave() {
    if (this.stopped) return
    for (const [pageId, entry] of this.pending) {
      this.send(pageId, entry, { keepalive: true }).catch(() => undefined)
    }
  }

  retryNow() {
    clearTimeout(this.retryTimer)
    if (this.state === "offline") this.retryIndex = 0
    this.run()
  }

  async run() {
    if (this.running || this.stopped) return
    this.running = true
    let blocked = false

    try {
      while (this.pending.size > 0 && !this.stopped) {
        const [pageId, entry] = this.pending.entries().next().value
        this.setState("saving")
        const token = this.onSend(pageId)
        const outcome = await this.send(pageId, entry)
        if (this.destroyed) return

        if (outcome === "superseded") {
          // Nothing to send again: the newer answers are the server's. A save
          // of the page queued meanwhile as a replay was made on top of the
          // same old answers, so it goes too; any other save is sent.
          const queued = this.pending.get(pageId)
          if (queued?.generation === entry.generation || queued?.replay)
            this.pending.delete(pageId)
          this.retryIndex = 0
          this.failedPages.delete(pageId)
          this.onSuperseded(pageId, token)
        } else if (outcome === "saved" || outcome === "failed") {
          if (this.pending.get(pageId)?.generation === entry.generation)
            this.pending.delete(pageId)
          if (outcome === "saved") {
            this.retryIndex = 0
            this.failedPages.delete(pageId)
            this.onSaved(pageId, token)
          } else if (!this.pending.has(pageId)) {
            // A newer edit already queued for the page is sent next: the
            // refusal of the older one says nothing about it.
            this.failedPages.add(pageId)
          }
        } else if (outcome === "offline" || outcome === "error") {
          // Kept queued and retried; a server error is not a lost connection.
          blocked = true
          this.setState(outcome === "offline" ? "offline" : "retrying")
          this.scheduleRetry()
          break
        } else {
          this.stopped = true
          this.setState(outcome)
          break
        }
      }
    } finally {
      this.running = false
    }

    if (!blocked && !this.stopped && this.pending.size === 0) {
      this.setState(this.failedPages.size > 0 ? "failed" : "saved")
    }
    this.settleWaiters()
  }

  async send(pageId, { form, complete, replay }, { keepalive = false } = {}) {
    const body = new FormData(form)
    body.set("direction", "stay")
    body.set("complete", complete ? "1" : "0")
    if (replay) body.set("replay", "1")
    const active = this.activePageId()
    if (active) body.set("active_page_id", active)

    // keepalive saves are handed to the browser as the page goes away; nothing
    // waits on them, so they get no timeout.
    const timeout = keepalive ? null : new AbortController()
    const timer = timeout && setTimeout(() => timeout.abort(), SAVE_TIMEOUT_MS)
    let response
    try {
      response = await patch(form.action, {
        body,
        responseKind: "turbo-stream",
        redirect: "manual",
        keepalive,
        signal: timeout?.signal,
      })
    } catch {
      // Timed out: the connection is up but unanswered — retried like a server
      // error. Anything else: no connection.
      return timeout?.signal.aborted ? "error" : "offline"
    } finally {
      clearTimeout(timer)
    }

    if (response.ok && response.headers.get(SAVED_HEADER) === pageId)
      return response.headers.get(SUPERSEDED_HEADER) ? "superseded" : "saved"
    // A server error or a gateway timeout, or a refusal the server marks as
    // temporary (a deadlock, a lost connection to the database): worth another
    // try, later.
    if (response.statusCode >= 500) return "error"

    const code = response.headers.get(ERROR_HEADER)
    if (code) return TERMINAL_ERRORS.has(code) ? "stopped" : "failed"
    // A redirect — the session ended and the app sent the request to sign in.
    if (
      response.statusCode === 0 ||
      response.response?.type === "opaqueredirect"
    )
      return "signed-out"
    if (response.statusCode === 401) return "signed-out"
    if (response.statusCode === 403 || response.statusCode === 404)
      return "stopped"
    return "error"
  }

  scheduleRetry() {
    if (this.destroyed) return
    clearTimeout(this.retryTimer)
    const delay =
      RETRY_DELAYS_MS[Math.min(this.retryIndex, RETRY_DELAYS_MS.length - 1)]
    this.retryIndex += 1
    this.retryTimer = setTimeout(() => this.run(), delay)
  }

  setState(state) {
    if (this.state === state) return
    this.state = state
    this.onState(state, { failedPageId: this.failedPageId })
  }

  settleWaiters() {
    if (this.running || this.waiters.length === 0) return
    const summary = {
      pending: this.pending.size,
      failed: Array.from(this.failedPages),
      stopped: this.stopped,
      state: this.state,
    }
    const waiters = this.waiters
    this.waiters = []
    waiters.forEach((settle) => settle(summary))
  }
}
